import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Upload, X, CircleNotch } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { setCategoryCache, getCategoryName, getSubcategoryName } from '../utils/categoryNames';
import { isEmailNotVerifiedError, showEmailVerificationRequiredToast } from '../utils/emailVerification';
import { SubcategoryInfoIcon } from '../components/SubcategoryInfoIcon';
import { getListingDurationDays } from '../utils/platformConfig';
import { NumberInput } from '../components/NumberInput';
import { resizeAndCompressListingImage } from '../utils/imageCompression';
import { validateListing } from '../utils/validateListing';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CreateListingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // ─── Base Global Fields ───
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [priceType, setPriceType] = useState('fixed');
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [region, setRegion] = useState('');
  const [city, setCity] = useState('');
  const [images, setImages] = useState([]);
  const [pendingUploads, setPendingUploads] = useState([]); // [{id, name, status: 'compressing'|'uploading'|'error'}]

  // ─── Dynamic Custom Fields (from admin) ───
  const [customFields, setCustomFields] = useState({});
  const [categoryFieldDefs, setCategoryFieldDefs] = useState([]);

  // ─── Validation ───
  const [errors, setErrors] = useState({});
  const clearError = (key) => setErrors((prev) => {
    if (!prev[key]) return prev;
    const { [key]: _, ...rest } = prev;
    return rest;
  });

  // ─── Data Sources ───
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [regionsData, setRegionsData] = useState({ regions: [], cities_by_region: {} });
  const [editLoaded, setEditLoaded] = useState(false);
  const editInitRef = useRef(false); // true once edit data has settled into all useEffects
  const pendingCustomFieldsRef = useRef(null); // stores custom fields until field defs are loaded

  const selectedCat = categories.find(c => c.value === category);

  // Tailwind helper: toggles the red border/ring when a field has an error
  const errBorder = (hasError) =>
    hasError
      ? 'border-danger focus:border-danger focus:ring-1 focus:ring-red-400'
      : 'border-border focus:border-primary focus:ring-1 focus:ring-primary';
  // Tiny helper: inline error label under a field
  const FieldError = ({ msg }) =>
    msg ? <p className="mt-1 text-xs text-danger" data-testid="field-error">{msg}</p> : null;

  // ─── Init: Fetch data, then listing if editing ───
  useEffect(() => {
    const init = async () => {
      await fetchData();
      if (id) await fetchListing();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ─── Category change → update subcategories ───
  useEffect(() => {
    if (category) {
      const cat = categories.find(c => c.value === category);
      setSubcategories(cat?.subcategories || []);
      if (!id) setSubcategory('');
    }
    // Only reset custom fields on user-initiated category change (not initial edit load)
    if (id && !editInitRef.current) return;
    setCustomFields({});
  }, [category, categories]);

  // ─── Fetch custom fields when category+subcategory change ───
  useEffect(() => {
    if (!category || !subcategory) {
      setCategoryFieldDefs([]);
      return;
    }
    axios.get(`${API}/category-fields/${category}/${subcategory}`)
      .then(res => {
        setCategoryFieldDefs(res.data.fields || []);
        // Apply pending custom fields from edit load
        if (pendingCustomFieldsRef.current) {
          setCustomFields(pendingCustomFieldsRef.current);
          pendingCustomFieldsRef.current = null;
          editInitRef.current = true;
        }
      })
      .catch(() => setCategoryFieldDefs([]));
    // Only reset custom fields on user-initiated subcategory change (not initial edit load)
    if (id && !editInitRef.current) return;
    setCustomFields({});
  }, [category, subcategory]);

  const fetchData = async () => {
    try {
      const [categoriesRes, citiesRes, regionsRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/cities`),
        axios.get(`${API}/regions`)
      ]);
      setCategories(categoriesRes.data);
      setCategoryCache(categoriesRes.data);
      setCities(citiesRes.data);
      setRegionsData(regionsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchListing = async () => {
    try {
      const { data } = await axios.get(`${API}/listings/${id}`);
      if (data.status === 'deleted') {
        toast.error(t('myListings.cannotEditDeleted') || 'Cannot edit a deleted listing. Restore it first.');
        navigate('/my-listings');
        return;
      }
      setTitle(data.title);
      setDescription(data.description);
      setPrice(parseInt(data.price_mga, 10) || '');
      setPriceType(data.price_type || 'fixed');
      setCategory(data.category);
      setSubcategory(data.subcategory || '');
      setCity(data.city);
      setRegion(data.region || '');
      setImages(data.images || []);

      // Store custom fields — they will be applied after field defs load
      if (data.custom_fields) {
        const cf = {};
        for (const [k, v] of Object.entries(data.custom_fields)) {
          cf[k] = v != null ? (Array.isArray(v) ? v : String(v)) : '';
        }
        pendingCustomFieldsRef.current = cf;
      }
      setEditLoaded(true);
      // If no subcategory, there are no custom field defs to wait for
      if (!data.subcategory) {
        editInitRef.current = true;
      }
    } catch (error) {
      toast.error('Failed to load listing');
      navigate('/my-listings');
    }
  };

  // ─── Computed: Cities filtered by region ───
  const citiesForRegion = region && regionsData.cities_by_region?.[region]
    ? regionsData.cities_by_region[region].sort()
    : cities;

  const handleRegionChange = (val) => {
    setRegion(val);
    setCity('');
  };

  // ─── Image Upload (client-side compress → upload, with per-image progress) ───
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (!files.length) return;
    if (images.length + pendingUploads.length + files.length > 8) {
      toast.error(t('listing.maxImages'));
      return;
    }
    // Reject obviously broken files up front; the compressor handles HEIC/JPEG/PNG/WebP
    // up to camera-raw sizes (8–10 MB) since we resize+compress before uploading.
    const validFiles = files.filter((f) => {
      if (f.size > 12 * 1024 * 1024) {
        toast.error(`${f.name}: trop volumineux (max 12 MB avant compression)`);
        return false;
      }
      return true;
    });
    if (!validFiles.length) return;

    setUploading(true);
    // Seed placeholders so the user sees a tile per file immediately.
    const placeholders = validFiles.map((f) => ({
      id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: f.name,
      status: 'compressing',
    }));
    setPendingUploads((prev) => [...prev, ...placeholders]);

    // Process files sequentially so progress feels deterministic and we don't
    // saturate slow Madagascar uplinks with parallel uploads.
    const uploadedPaths = [];
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      const phId = placeholders[i].id;
      try {
        // Step 1 — client-side resize + WebP compress (~1200 px max, 200–400 KB)
        const compressed = await resizeAndCompressListingImage(file);
        setPendingUploads((prev) => prev.map((p) => p.id === phId ? { ...p, status: 'uploading' } : p));

        // Step 2 — upload the compressed file
        const formData = new FormData();
        formData.append('file', compressed);
        const { data } = await axios.post(`${API}/upload`, formData, {
          withCredentials: true,
          timeout: 120000,
        });
        uploadedPaths.push(data.path);
        setPendingUploads((prev) => prev.filter((p) => p.id !== phId));
      } catch (error) {
        const detail = error.response?.data?.detail || error.message || 'Échec de l\'upload';
        toast.error(`${file.name}: ${detail}`);
        setPendingUploads((prev) => prev.map((p) => p.id === phId ? { ...p, status: 'error' } : p));
        // Auto-clear error placeholders after a short delay
        setTimeout(() => {
          setPendingUploads((prev) => prev.filter((p) => p.id !== phId));
        }, 3500);
      }
    }
    if (uploadedPaths.length) {
      setImages((prev) => [...prev, ...uploadedPaths]);
    }
    setUploading(false);
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  // ─── Submit ───
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Run validation before touching the network
    const validationErrors = validateListing(
      { title, description, price, category, region, city, images, customFields },
      categoryFieldDefs,
      t,
    );

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error(t('validation.fixErrors'));
      // Scroll to the first invalid field in the order they appear in the DOM
      requestAnimationFrame(() => {
        const firstKey = Object.keys(validationErrors)[0];
        const el = document.querySelector(`[data-error-key="${firstKey}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }
    setErrors({});

    setLoading(true);
    try {
      const payload = {
        title, description,
        price_mga: typeof price === 'number' ? price : parseInt(String(price).replace(/\D/g, ''), 10) || 0,
        price_type: priceType,
        category, subcategory, region, city,
        images,
      };

      // Include dynamic custom fields
      if (Object.keys(customFields).length > 0) {
        const cleanCustom = {};
        for (const [k, v] of Object.entries(customFields)) {
          if (v === '' || v == null) continue;
          cleanCustom[k] = v;
        }
        if (Object.keys(cleanCustom).length > 0) {
          payload.custom_fields = cleanCustom;
        }
      }

      if (id) {
        await axios.put(`${API}/listings/${id}`, payload, { withCredentials: true });
        toast.success(t('listing.updated'));
        navigate(`/listings/${id}`);
      } else {
        const { data: newListing } = await axios.post(`${API}/listings`, payload, { withCredentials: true });
        const days = await getListingDurationDays();
        toast.success(t('listing.published', { days }));
        navigate(`/listings/${newListing.listing_id}`);
      }
    } catch (error) {
      if (isEmailNotVerifiedError(error)) {
        showEmailVerificationRequiredToast(t);
      } else {
        toast.error('Failed to save listing');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream pb-20 md:pb-0">
      <Header />

      <div className="max-w-3xl mx-auto p-6">
        <h1 className="text-lg md:text-xl font-medium text-text_strong tracking-wide mb-6" data-testid="page-title">
          {id ? t('listing.editListing') : t('listing.createListing')}
        </h1>

        <form onSubmit={handleSubmit}>
          <div className="bg-white border border-border rounded-xl p-6 space-y-5">

            {/* Title */}
            <div data-error-key="title">
              <Label>{t('listing.title')} *</Label>
              <Input
                value={title}
                onChange={(e) => { setTitle(e.target.value); clearError('title'); }}
                placeholder={t('listing.titlePlaceholder')}
                className={`border ${errBorder(!!errors.title)}`}
                data-testid="title-input"
              />
              <FieldError msg={errors.title} />
            </div>

            {/* Price + Price Type */}
            <div data-error-key="price">
              <Label>{t('listing.price')} *</Label>
              <div className="flex gap-3">
                <NumberInput
                  value={price}
                  onChange={(n) => { setPrice(n); clearError('price'); }}
                  placeholder="500 000"
                  className={`flex-1 border ${errBorder(!!errors.price)}`}
                  data-testid="price-input"
                />
                <div className="flex rounded-lg border border-border overflow-hidden shrink-0" data-testid="price-type-toggle">
                  <button type="button" onClick={() => setPriceType('fixed')}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${priceType === 'fixed' ? 'bg-primary text-white' : 'bg-white text-text_muted hover:bg-surface_muted'}`}
                    data-testid="price-type-fixed">{t('listing.priceFixed')}</button>
                  <button type="button" onClick={() => setPriceType('negotiable')}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${priceType === 'negotiable' ? 'bg-primary text-white' : 'bg-white text-text_muted hover:bg-surface_muted'}`}
                    data-testid="price-type-negotiable">{t('listing.priceNegotiable')}</button>
                </div>
              </div>
              <FieldError msg={errors.price} />
            </div>

            {/* Category + Subcategory */}
            <div className="grid md:grid-cols-2 gap-4">
              <div data-error-key="category">
                <Label>{t('listing.category')} *</Label>
                <Select value={category} onValueChange={(v) => { setCategory(v); clearError('category'); }}>
                  <SelectTrigger data-testid="category-select" className={errors.category ? 'border-danger' : ''}>
                    <SelectValue placeholder={t('listing.category')} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{getCategoryName(cat.value, t)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError msg={errors.category} />
              </div>
              {subcategories.length > 0 && (
                <div>
                  <Label>{t('listing.subcategory')}</Label>
                  <Select value={subcategory} onValueChange={setSubcategory}>
                    <SelectTrigger data-testid="subcategory-select">
                      <SelectValue placeholder={t('listing.subcategory')}>
                        {subcategory ? getSubcategoryName(subcategory, t) : t('listing.subcategory')}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {subcategories.map((sub) => (
                        <SelectItem key={sub.value} value={sub.value} data-testid={`subcategory-option-${sub.value}`}>
                          <span className="inline-flex items-center">
                            {getSubcategoryName(sub.value, t)}
                            <SubcategoryInfoIcon description={sub.description} />
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Dynamic Custom Fields — loaded per subcategory from admin config */}
            {categoryFieldDefs.length > 0 && (
              <div className="border-t border-border pt-5 space-y-4" data-testid="custom-fields-section">
                <h3 className="text-base font-medium text-text_strong tracking-wide">
                  {getCategoryName(category, t)} — {t('listing.filters')}
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {categoryFieldDefs.map((fieldDef) => {
                    const val = customFields[fieldDef.key] || '';
                    const setVal = (v) => {
                      setCustomFields(prev => ({ ...prev, [fieldDef.key]: v }));
                      clearError(fieldDef.key);
                    };
                    const err = errors[fieldDef.key];

                    if (fieldDef.type === 'dropdown') {
                      return (
                        <div key={fieldDef.key} data-error-key={fieldDef.key}>
                          <Label>{fieldDef.label}{fieldDef.required ? ' *' : ''}</Label>
                          <Select value={val} onValueChange={setVal}>
                            <SelectTrigger data-testid={`custom-${fieldDef.key}-select`} className={err ? 'border-danger' : ''}>
                              <SelectValue placeholder={fieldDef.placeholder || fieldDef.label} />
                            </SelectTrigger>
                            <SelectContent>
                              {(fieldDef.options || []).map(opt => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FieldError msg={err} />
                        </div>
                      );
                    }

                    if (fieldDef.type === 'multi_select') {
                      const selected = Array.isArray(val) ? val : (val ? [val] : []);
                      return (
                        <div key={fieldDef.key} data-error-key={fieldDef.key}>
                          <Label>{fieldDef.label}{fieldDef.required ? ' *' : ''}</Label>
                          <div className={`flex flex-wrap gap-2 mt-1 ${err ? 'rounded-md border border-danger p-2' : ''}`}>
                            {(fieldDef.options || []).map(opt => (
                              <button key={opt} type="button"
                                onClick={() => {
                                  const newSel = selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt];
                                  setVal(newSel);
                                }}
                                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${selected.includes(opt) ? 'border-primary bg-primary/10 text-primary' : 'border-border text-text_muted hover:border-border'}`}
                                data-testid={`custom-${fieldDef.key}-${opt}`}
                              >{opt}</button>
                            ))}
                          </div>
                          <FieldError msg={err} />
                        </div>
                      );
                    }

                    if (fieldDef.type === 'checkbox') {
                      return (
                        <div key={fieldDef.key} className="flex items-center gap-2 mt-2" data-error-key={fieldDef.key}>
                          <input type="checkbox" checked={val === 'true' || val === true}
                            onChange={(e) => setVal(e.target.checked ? 'true' : 'false')}
                            className="rounded border-border" data-testid={`custom-${fieldDef.key}-checkbox`} />
                          <Label className="cursor-pointer">{fieldDef.label}</Label>
                          <FieldError msg={err} />
                        </div>
                      );
                    }

                    if (fieldDef.type === 'date') {
                      return (
                        <div key={fieldDef.key} data-error-key={fieldDef.key}>
                          <Label>{fieldDef.label}{fieldDef.required ? ' *' : ''}</Label>
                          <Input type="date" value={val} onChange={(e) => setVal(e.target.value)}
                            className={`border ${errBorder(!!err)}`}
                            data-testid={`custom-${fieldDef.key}-date`} />
                          <FieldError msg={err} />
                        </div>
                      );
                    }

                    return (
                      <div key={fieldDef.key} data-error-key={fieldDef.key}>
                        <Label>{fieldDef.label}{fieldDef.required ? ' *' : ''}</Label>
                        <Input
                          type={fieldDef.type === 'number' ? 'number' : 'text'}
                          value={val} onChange={(e) => setVal(e.target.value)}
                          placeholder={fieldDef.placeholder || ''}
                          min={fieldDef.validation?.min} max={fieldDef.validation?.max}
                          className={`border ${errBorder(!!err)}`}
                          data-testid={`custom-${fieldDef.key}-input`} />
                        <FieldError msg={err} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Region & City */}
            <div className="grid md:grid-cols-2 gap-4">
              <div data-error-key="region">
                <Label>{t('realEstate.region')} *</Label>
                <Select value={region} onValueChange={(v) => { handleRegionChange(v); clearError('region'); }}>
                  <SelectTrigger data-testid="region-select" className={errors.region ? 'border-danger' : ''}>
                    <SelectValue placeholder={t('realEstate.region')} />
                  </SelectTrigger>
                  <SelectContent>
                    {regionsData.regions.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError msg={errors.region} />
              </div>
              <div data-error-key="city">
                <Label>{t('listing.city')} *</Label>
                <Select value={city} onValueChange={(v) => { setCity(v); clearError('city'); }} disabled={!region}>
                  <SelectTrigger data-testid="city-select" className={`${!region ? 'opacity-50' : ''} ${errors.city ? 'border-danger' : ''}`}>
                    <SelectValue placeholder={region ? t('listing.city') : t('realEstate.region') + '...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {citiesForRegion.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError msg={errors.city} />
              </div>
            </div>

            {/* Description */}
            <div data-error-key="description">
              <Label>{t('listing.description')} *</Label>
              <Textarea
                value={description}
                onChange={(e) => { setDescription(e.target.value); clearError('description'); }}
                placeholder={t('listing.descriptionPlaceholder')} rows={5}
                className={`border resize-none ${errBorder(!!errors.description)}`}
                data-testid="description-input"
              />
              <FieldError msg={errors.description} />
            </div>

            {/* Images */}
            <div data-error-key="images">
              <Label>{t('listing.images')} * ({images.length}/8)</Label>
              <div className="mt-2">
                <div className="grid grid-cols-4 gap-3 mb-3">
                  {images.map((img, idx) => (
                    <div key={img} className="aspect-[4/3] rounded-lg border border-border overflow-hidden relative group">
                      <img src={`${API}/files/${img}`} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeImage(idx)}
                        className="absolute top-1 right-1 bg-danger text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-image-${idx}`}>
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  {/* Pending tiles — one per in-flight upload, with status hint */}
                  {pendingUploads.map((p) => (
                    <div
                      key={p.id}
                      className={`aspect-[4/3] rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1.5 ${
                        p.status === 'error' ? 'border-danger bg-danger-soft' : 'border-primary/40 bg-primary/5'
                      }`}
                      data-testid={`pending-image-${p.status}`}
                    >
                      {p.status === 'error' ? (
                        <X size={20} className="text-danger" />
                      ) : (
                        <CircleNotch size={20} className="text-primary animate-spin" />
                      )}
                      <span className="text-[10px] text-text_muted px-1 truncate max-w-full">
                        {p.status === 'compressing' && t('listing.compressing')}
                        {p.status === 'uploading' && t('listing.uploading')}
                        {p.status === 'error' && t('common.error')}
                      </span>
                    </div>
                  ))}
                </div>
                {(images.length + pendingUploads.length) < 8 && (
                  <label className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${uploading ? 'border-primary bg-primary/5' : 'border-border hover:border-primary'}`}>
                    <Upload size={32} className={uploading ? 'text-primary animate-pulse' : 'text-text_muted'} />
                    <span className="text-sm mt-2 text-text_muted">
                      {uploading ? t('listing.uploading') : t('listing.uploadImages')}
                    </span>
                    <span className="text-xs text-text_muted mt-1">JPG, PNG, WEBP — 1200x900 px (4:3)</span>
                    <input type="file" multiple onChange={(e) => { handleImageUpload(e); clearError('images'); }} disabled={uploading} className="hidden" id="image-upload"
                      accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp" data-testid="image-upload-input" />
                  </label>
                )}
              </div>
              <FieldError msg={errors.images} />
            </div>

            {/* Submit */}
            <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary-hover text-white py-3 font-semibold" data-testid="submit-listing">
              {loading ? t('common.loading') : id ? t('listing.updateListing') : t('listing.publishListing')}
            </Button>
          </div>
        </form>
      </div>

      <BottomNav />
    </div>
  );
};
